import { Button, ButtonMenu, Css, useModal } from "@homebound/beam";
import { createPlanPackageVersionHistoryUrl } from "src/RouteUrls";
import { ReadyPlanVersionHeader } from "src/components/ReadyPlanVersionHeader";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { PlanPackagePageV2_PlanPackageFragment } from "src/routes/libraries/plan-package-v2/PlanPackagePageV2.generated";
import { ArchivePlanPackageModal } from "src/routes/libraries/plan-package/details/components/ArchivePlanPackageModal";
import { useConfirmDiscardDraftVersionModalButtonMenuItem } from "src/routes/libraries/plan-package/details/components/ConfirmDiscardDraftVersionModal";
import { CopyPlanPackageModal } from "src/routes/libraries/plan-package/details/components/CopyPlanPackageModal";
import { PublishAggregateVersionModal } from "src/routes/libraries/plan-package/details/components/PublishAggregateVersionModal";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import { planPackageOverviewRoute } from "src/routing/domains/librariesPaths";
import { fail } from "src/utils";

type PlanPackageHeaderActionsProps = { planPackage: PlanPackagePageV2_PlanPackageFragment };

export function PlanPackageHeaderActions({ planPackage }: PlanPackageHeaderActionsProps) {
  const { openModal } = useModal();
  const { planPackageVersionId } = useRouteParams<PlanPackageDetailsParams>();
  const { id, name, status, aggregateDraft, aggregateLatest } = planPackage;
  const discardDraftMenuItem = useConfirmDiscardDraftVersionModalButtonMenuItem(
    planPackage,
    (planPackageId, versionId) => planPackageOverviewRoute.url({ planPackageId, planPackageVersionId: versionId }),
  );

  return (
    <div css={Css.df.aic.gap1.$}>
      <ReadyPlanVersionHeader readyPlan={planPackage} versionId={planPackageVersionId} />
      <Button
        size="md"
        label="Publish"
        disabled={!aggregateDraft?.canPublish.allowed}
        tooltip={disableBasedOnPotentialOperation(aggregateDraft?.canPublish)}
        onClick={() =>
          openModal({
            content: (
              <PublishAggregateVersionModal
                draftAggregateVersion={aggregateDraft ?? fail("No draft aggregate version found")}
                copyText="This update will be made available to any Design Package and Product Offering that reference it."
                readyPlanName={name}
                readyPlanId={id}
                allowGenerateAiNotes
              />
            ),
          })
        }
      />
      <ButtonMenu
        trigger={{ icon: "verticalDots", variant: "outline" }}
        items={[
          { label: "Version History", onClick: createPlanPackageVersionHistoryUrl(id, aggregateLatest.id) },
          {
            label: "Archive",
            onClick: () => openModal({ content: <ArchivePlanPackageModal planPackageId={id} /> }),
            disabled: status.code === ReadyPlanStatus.Archived ? "Plan is already Archived" : false,
          },
          discardDraftMenuItem,
          {
            label: "Make a copy",
            onClick: () => openModal({ content: <CopyPlanPackageModal planPackage={planPackage} />, size: "lg" }),
          },
        ]}
      />
    </div>
  );
}
